import { motion } from "framer-motion";
import {
  FiEye,
  FiGlobe,
  FiShield,
  FiStar,
  FiArrowRight,
  FiTarget,
  FiHeart,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";

const Vision = () => {
  const { t } = useTranslation();

  const visionElements = [
    {
      icon: FiEye,
      title: t("vision.elements.clearVision.title"),
      description: t("vision.elements.clearVision.description"),
      color: "from-blue-500 to-blue-600",
    },
    {
      icon: FiGlobe,
      title: t("vision.elements.globalImpact.title"),
      description: t("vision.elements.globalImpact.description"),
      color: "from-green-500 to-green-600",
    },
    {
      icon: FiShield,
      title: t("vision.elements.protection.title"),
      description: t("vision.elements.protection.description"),
      color: "from-purple-500 to-purple-600",
    },
    {
      icon: FiStar,
      title: t("vision.elements.excellence.title"),
      description: t("vision.elements.excellence.description"),
      color: "from-orange-500 to-orange-600",
    },
  ];

  const visionGoals = [
    { icon: FiTarget, text: t("vision.goals.resilient") },
    { icon: FiHeart, text: t("vision.goals.empowered") },
    { icon: FiGlobe, text: t("vision.goals.sustainable") },
  ];

  return (
    <section
      id="vision"
      className="relative overflow-x-hidden bg-gradient-to-br from-gray-900 via-green-900 to-gray-900"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 overflow-hidden">
        <div className="absolute top-20 right-4 h-40 w-40 rounded-full bg-green-400"></div>
        <div className="absolute top-60 left-4 h-32 w-32 rounded-full bg-blue-400"></div>
        <div className="absolute bottom-20 right-1/4 h-24 w-24 rounded-full bg-purple-400"></div>
      </div>

      <div className="relative px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-7xl">
          {/* Header Section */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center space-x-3 mb-6">
              <div className="h-1 w-12 bg-gradient-to-r from-green-400 to-blue-400"></div>
              <span className="font-semibold tracking-wide text-green-400 uppercase text-sm">
                {t("vision.title")}
              </span>
              <div className="h-1 w-12 bg-gradient-to-r from-blue-400 to-green-400"></div>
            </div>
            <h2 className="text-4xl leading-tight font-bold text-white md:text-6xl mb-6">
              {t("vision.subtitle")}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-blue-400 mx-auto"></div>
          </motion.div>

          <div className="grid items-center gap-16 lg:grid-cols-2 overflow-hidden">
            {/* Image Section */}
            <motion.div
              className="relative order-2 lg:order-1"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="relative">
                <div className="absolute -inset-6 rounded-3xl bg-gradient-to-r from-green-400/20 to-blue-400/20 blur-2xl"></div>
                <div className="relative overflow-hidden rounded-2xl border-4 border-white/20 shadow-2xl">
                  <img
                    className="h-96 w-full object-cover"
                    src="/images/image_2.jpg"
                    alt="ACDHA Vision"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent"></div>

                  {/* Overlay Content */}
                  <div className="absolute top-6 left-6 right-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                      <h4 className="text-lg font-bold text-white mb-2">
                        2030 Vision Goal
                      </h4>
                      <p className="text-sm text-green-200">
                        Building a sustainable future for all
                      </p>
                    </div>
                  </div>
                </div>

                {/* Floating Goals */}
                <div className="absolute right-0 bottom-0 space-y-3 max-w-[calc(100%-2rem)] overflow-hidden">
                  {visionGoals.map((goal, index) => (
                    <motion.div
                      key={index}
                      className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <div className="flex items-center space-x-2">
                        <goal.icon className="h-4 w-4 text-green-400" />
                        <span className="text-xs text-white font-medium">
                          {goal.text}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Content Section */}
            <motion.div
              className="order-1 space-y-8 lg:order-2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {/* Vision Statement */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-400/10 to-blue-400/10 rounded-2xl blur-xl"></div>
                <div className="relative rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-r from-green-400 to-blue-400 flex items-center justify-center">
                        <FiTarget className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-4">
                        {t("vision.statement.title")}
                      </h3>
                      <p className="text-lg leading-relaxed text-gray-200">
                        {t("vision.statement.description")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vision Elements */}
              <div className="grid grid-cols-2 gap-4 overflow-hidden">
                {visionElements.map((element, index) => (
                  <motion.div
                    key={index}
                    className="group relative"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="relative rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 text-center hover:bg-white/10 hover:border-white/20 transition-all duration-300 group-hover:scale-105">
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative">
                        <div
                          className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r ${element.color} group-hover:scale-110 transition-transform duration-300`}
                        >
                          <element.icon className="h-7 w-7 text-white" />
                        </div>
                        <h3 className="mb-2 text-sm font-bold text-white">
                          {element.title}
                        </h3>
                        <p className="text-xs leading-relaxed text-gray-300">
                          {element.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Call to Action */}
              <motion.div
                className="pt-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="flex flex-col sm:flex-row gap-4">
                  <button className="group flex items-center justify-center space-x-2 transform rounded-lg bg-gradient-to-r from-green-600 to-blue-600 px-8 py-4 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-green-500 hover:to-blue-500">
                    <span>{t("vision.cta.joinMission")}</span>
                    <FiArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                  <button className="transform rounded-lg border-2 border-white/30 bg-white/10 backdrop-blur-sm px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-white/20 hover:border-white/50">
                    {t("vision.cta.learnMore")}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Vision;
